import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, isToday } from "date-fns";
import { parseLocalDateOnly } from "@/lib/dateUtils";

type RefreshPayload = {
  skipped?: boolean;
  guest_left?: boolean;
  check_out_time?: string | null;
  check_in_time?: string | null;
  same_day?: boolean;
} | null;

async function invokeRefresh(propertyId: string, taskId: string): Promise<RefreshPayload> {
  const { data, error } = await supabase.functions.invoke("hospitable-proxy", {
    body: {
      action: "refresh_turnover_window",
      property_id: propertyId,
      cleaning_task_id: taskId,
    },
  });
  if (error) throw error;
  return (data as RefreshPayload) ?? null;
}

/**
 * Live-refresh departing/arriving clock times + guest-left from Hospitable
 * when a cleaner/owner opens a job. Throttled by React Query staleTime.
 */
export function useRefreshTurnoverWindow(opts: {
  propertyId?: string | null;
  taskId?: string | null;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const enabled = Boolean(opts.enabled && opts.propertyId && opts.taskId);

  return useQuery({
    queryKey: ["refresh-turnover-window", opts.propertyId, opts.taskId],
    queryFn: async () => {
      const payload = await invokeRefresh(opts.propertyId!, opts.taskId!);
      if (payload && !payload.skipped) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["current-cleaning-task"] }),
          queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner"] }),
          queryClient.invalidateQueries({ queryKey: ["my-tasks"] }),
          queryClient.invalidateQueries({ queryKey: ["reservations", opts.propertyId] }),
        ]);
      }
      return payload;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
}

/** Refresh same-day / today jobs on the Jobs list so times show before open. */
export function useRefreshVisibleTurnoverWindows(
  tasks: Array<{ id: string; property_id: string; same_day?: boolean; scheduled_date: string }>,
) {
  const queryClient = useQueryClient();
  const targets = tasks
    .filter((t) => {
      if (t.same_day) return true;
      try {
        return isToday(parseLocalDateOnly(t.scheduled_date));
      } catch {
        return false;
      }
    })
    .slice(0, 8);
  const key = targets.map((t) => t.id).sort().join(",");

  return useQuery({
    queryKey: ["refresh-visible-turnover-windows", key],
    queryFn: async () => {
      let wrote = false;
      for (const t of targets) {
        try {
          const payload = await invokeRefresh(t.property_id, t.id);
          if (payload && !payload.skipped) wrote = true;
        } catch {
          /* keep going — one unit must not block the list */
        }
      }
      if (wrote) {
        await queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
        const propertyIds = [...new Set(targets.map((t) => t.property_id))];
        await Promise.all(
          propertyIds.map((id) =>
            queryClient.invalidateQueries({ queryKey: ["reservations", id] }),
          ),
        );
      }
      return { refreshed: targets.length, wrote };
    },
    enabled: targets.length > 0,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
}

/**
 * Owner Schedule tab — refresh today's / same-day turnovers so List + Month
 * both pick up Guest left / Waiting from Hospitable without a manual Sync.
 */
export function useRefreshScheduleTurnoverWindows(
  propertyId: string | undefined,
  tasks: Array<{ id: string; scheduled_date: string; status?: string | null; reservation?: { end_date?: string } | null }>,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const today = format(new Date(), "yyyy-MM-dd");
  const targets = tasks
    .filter((t) => {
      if (t.status === "completed") return false;
      const end = t.reservation?.end_date || t.scheduled_date;
      return end?.slice(0, 10) === today;
    })
    .slice(0, 8);
  const key = targets.map((t) => t.id).sort().join(",");

  return useQuery({
    queryKey: ["refresh-schedule-turnover-windows", propertyId, key],
    queryFn: async () => {
      if (!propertyId) return { refreshed: 0, wrote: false };
      let wrote = false;
      for (const t of targets) {
        try {
          const payload = await invokeRefresh(propertyId, t.id);
          if (payload && !payload.skipped) wrote = true;
        } catch {
          /* keep going */
        }
      }
      if (wrote) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] }),
          queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] }),
        ]);
      }
      return { refreshed: targets.length, wrote };
    },
    enabled: Boolean(enabled && propertyId && targets.length > 0),
    staleTime: 60 * 1000,
    retry: false,
  });
}
